import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyAttachmentTypeView, CompanyAttachmentTypeInput } from '../../../models/general';
import { getCompanyAttachmentTypes, addCompanyAttachmentTypes, editCompanyAttachmentTypes } from '../../../api/General/attachment';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../../../components/ui/Table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppIcon from '../../../components/AppIcon';
import { Spinner } from '../../../components/ui/Spinner';
import Pagination from '../../../components/Pagination';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { useDialogError, DialogErrorAlert } from '../../../components/ui/DialogError';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Paperclip } from 'lucide-react';

export default function AttachmentTypes() {
  const { t } = useTranslation('common');
  const { toast, showToast, dismissToast } = useToast();
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [filteredAttachmentTypes, setFilteredAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [count, setCount] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50];
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'add' | 'edit'>('add');
  const [currentInput, setCurrentInput] = useState<CompanyAttachmentTypeInput>({ attachmentType: '', reminderDays: 0 });
  const [editId, setEditId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // CO-49: a refused write explains itself inside the dialog, not only in a toast.
  const dialogError = useDialogError();

  const nameError = currentInput.attachmentType.trim().length === 0;
  const reminderError = Number.isNaN(currentInput.reminderDays) || currentInput.reminderDays < 0;
  const formInvalid = nameError || reminderError || loading;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCompanyAttachmentTypes();
      setAttachmentTypes(response.payload);
      setFilteredAttachmentTypes(response.payload);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredAttachmentTypes(attachmentTypes);
      setCurrentPage(1);
      return;
    }
    const lower = search.toLowerCase();
    setFilteredAttachmentTypes(
      attachmentTypes.filter(a => a.attachmentType?.toLowerCase().includes(lower))
    );
    setCurrentPage(1);
  }, [search, attachmentTypes]);

  const handleOpenDialog = (type: 'add' | 'edit', row?: CompanyAttachmentTypeView) => {
    setDialogType(type);
    if (type === 'edit' && row) {
      setCurrentInput({ attachmentType: row.attachmentType, reminderDays: row.reminderDays });
      setEditId(row.id);
    } else {
      setCurrentInput({ attachmentType: '', reminderDays: 0 });
      setEditId(null);
    }
    dialogError.clear();
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    if (formInvalid) return;
    setLoading(true);
    dialogError.clear();
    try {
      if (dialogType === 'add') {
        await addCompanyAttachmentTypes([currentInput]);
      } else if (dialogType === 'edit' && editId != null) {
        await editCompanyAttachmentTypes([{ ...(currentInput as any), id: editId }]);
      }
      setOpenDialog(false);
      await fetchData();
      showToast(t('Saved successfully'), 'success');
    } catch (error: any) {
      const parsed = dialogError.capture(error, t('An unexpected error occurred'));
      showToast(parsed.messages[0], 'error');
    } finally {
      setLoading(false);
    }
  };

  const paginatedData = useMemo(() => filteredAttachmentTypes.slice(
    (currentPage - 1) * count,
    Math.min(currentPage * count, filteredAttachmentTypes.length)
  ), [filteredAttachmentTypes, currentPage, count]);

  const totalPages = useMemo(() => Math.ceil(filteredAttachmentTypes.length / count), [filteredAttachmentTypes, count]);

  const pageSizeOptions_ = pageSizeOptions.map(opt => ({ value: opt, label: String(opt) }));

  return (
    <PageLayout
      title={t('Attachment Types')}
      icon={Paperclip}
      accentColor="slate"
      headerActions={(
        <div className="flex items-center gap-2">
          <div className="relative">
            <AppIcon name="Search" size={16} className="absolute start-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder={t('Search') || 'Search'}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
              className="ps-8"
              aria-label={t('Search Attachment Types') || 'Search Attachment Types'}
            />
          </div>
          <Button
            variant="outline"
            iconName="PlusCircle"
            onClick={() => handleOpenDialog('add')}
            title={t('Add Attachment Type') || ''}
          >
            {t('Add')}
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {t('Showing')}: {paginatedData.length} / {filteredAttachmentTypes.length}
        </p>
        <Select
          label={t('Rows per page')}
          options={pageSizeOptions_}
          value={count}
          onChange={(val: any) => { setCount(Number(val)); setCurrentPage(1); }}
          className="min-w-[120px]"
        />
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Attachment Type')}</TableHead>
              <TableHead>{t('Reminder Days')}</TableHead>
              <TableHead className="w-[100px]">{t('Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  <Spinner size="sm" />
                </TableCell>
              </TableRow>
            )}
            {!loading && paginatedData.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-6 text-muted-foreground">
                  {search ? t('No results match your search') : t('No attachment types yet')}
                </TableCell>
              </TableRow>
            )}
            {!loading && paginatedData.map(row => (
              <TableRow key={row.id || row.attachmentType}>
                <TableCell><strong>{row.attachmentType}</strong></TableCell>
                <TableCell>{row.reminderDays}</TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    title={t('Edit') || ''}
                    aria-label={t('Edit Attachment Type') || 'edit attachment type'}
                    onClick={() => handleOpenDialog('edit', row)}
                  >
                    <AppIcon name="Pencil" size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      <Dialog open={openDialog} onOpenChange={(v) => { if (loading) return; if (!v) dialogError.clear(); setOpenDialog(v); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'add' ? t('Add Attachment Type') : t('Edit Attachment Type')}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <DialogErrorAlert error={dialogError.error} onDismiss={dialogError.clear} className="mb-0" />
            <Input
              autoFocus
              label={t('Attachment Type')}
              value={currentInput.attachmentType}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentInput({ ...currentInput, attachmentType: e.target.value })}
              error={nameError ? t('Required') : undefined}
            />
            <Input
              label={t('Reminder Days')}
              type="number"
              value={currentInput.reminderDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentInput({ ...currentInput, reminderDays: parseInt(e.target.value, 10) || 0 })}
              error={reminderError ? t('Must be a non-negative number') : undefined}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} disabled={loading}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={formInvalid}>
              {loading ? <Spinner size="sm" /> : t('Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
